"use client";

import type { ReactNode } from "react";
import { useTranslations } from "next-intl";

interface EventsFeedSkeletonProps {
  view?: "list" | "grid";
  fillWidth?: boolean;
  headerTrailing?: ReactNode;
  showDateHeader?: boolean;
}

export function EventsFeedSkeleton({
  view = "list",
  fillWidth,
  headerTrailing,
  showDateHeader = true,
}: EventsFeedSkeletonProps) {
  const t = useTranslations("Events");

  return (
    <div role="status" aria-label={t("loadingShows")}>
      <span className="sr-only">{t("loadingShows")}</span>
      {showDateHeader && (
        <div className="mb-4 flex h-6 items-center gap-4 pl-2 lg:pl-0">
          <div aria-hidden="true" className="h-4 w-40 rounded bg-neutral-800 motion-safe:animate-pulse" />
          <div aria-hidden="true" className="h-px flex-1 bg-neutral-700/70" />
          {headerTrailing}
        </div>
      )}
      <div
        aria-hidden="true"
        className={view === "list"
          ? `grid grid-cols-1 gap-4 ${fillWidth ? "" : "mx-auto max-w-3xl sm:pt-4 lg:max-w-5xl"}`
          : "grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"}
      >
        {Array.from({ length: view === "list" ? 4 : 6 }, (_, index) => (
          <div key={index} className="overflow-hidden rounded-lg border border-neutral-700 bg-surface motion-safe:animate-pulse">
            <div className={view === "list" ? "lg:grid lg:min-h-40 lg:grid-cols-[14rem_minmax(0,1fr)_14rem]" : ""}>
              <div className={view === "list" ? "hidden bg-neutral-900 lg:block" : "aspect-[16/9] bg-neutral-900"} />
              {view === "list" && (
                <div className="lg:hidden">
                  <div className="space-y-2 border-b border-neutral-800 px-4 py-3">
                    <div className="h-3 w-36 rounded bg-neutral-800" />
                    <div className="h-3 w-48 rounded bg-neutral-800" />
                  </div>
                  <div className="aspect-[4/3] max-h-[22rem] bg-neutral-900" />
                  <div className="flex gap-2 px-4 py-2.5">
                    <div className="h-5 w-16 rounded-full bg-neutral-800" />
                    <div className="h-5 w-20 rounded-full bg-neutral-800" />
                  </div>
                </div>
              )}
              <div className={`space-y-4 px-4 py-5 lg:px-5 ${view === "list" ? "hidden lg:block lg:py-4" : ""}`}>
                {view !== "list" && <div className="h-3 w-2/3 rounded bg-neutral-800" />}
                <div className="h-4 w-3/5 rounded bg-neutral-800" />
                <div className="flex gap-2">
                  <div className="h-5 w-16 rounded-full bg-neutral-800" />
                  <div className="h-5 w-20 rounded-full bg-neutral-800" />
                </div>
              </div>
              {view === "list" && (
                <div className="hidden border-l border-neutral-800/30 px-4 pb-4 pt-5 lg:flex lg:flex-col">
                  <div className="h-4 w-28 rounded bg-neutral-800" />
                  <div className="mt-2 h-3 w-20 rounded bg-neutral-800" />
                  <div className="mt-auto h-4 w-4 self-end rounded-full bg-neutral-800" />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
